import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, X } from 'lucide-react'

import { FieldLabel } from '@/components/FieldLabel'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import type { Role } from '@/services/users/rolesApi'
import {
  canHoldTeamGroupRoles,
  emptyAppRoles,
  networkRolesFor,
  teamRolesFor,
  type AppRolesDraft,
  type RolesDraft,
} from '@/services/users/userRoles'

const NONE = '__none__'

export interface RoleTarget {
  id: string
  name: string
  description?: string
  type?: string
}

/** A select of roles, with an empty choice when `emptyLabel` is given. */
function RoleSelect({
  value,
  roles,
  emptyLabel,
  label,
  disabled,
  onChange,
}: {
  value: string
  roles: Role[]
  emptyLabel?: string
  label: string
  disabled?: boolean
  onChange: (roleId: string) => void
}) {
  return (
    <Select
      label={label}
      value={value || NONE}
      disabled={disabled}
      onValueChange={(v) => onChange(v === NONE ? '' : v)}
    >
      <SelectTrigger>
        <SelectValue>{roles.find((r) => r.id === value)?.name ?? emptyLabel ?? null}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {emptyLabel && <SelectItem value={NONE}>{emptyLabel}</SelectItem>}
        {roles.map((role) => (
          <SelectItem key={role.id} value={role.id}>
            {role.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

/**
 * The teams or team groups the user holds a role on, each with its role, and a
 * search to add another. Only the held ones are listed, so a network with a
 * hundred teams stays a short list.
 */
function TargetRoles({
  title,
  targets,
  roles,
  app,
  catalog,
  addLabel,
  searchPlaceholder,
  emptyText,
  disabled,
  onChange,
}: {
  title: string
  targets: RoleTarget[]
  roles: Record<string, string>
  app: string
  catalog: Role[]
  addLabel: string
  searchPlaceholder: string
  emptyText: string
  disabled?: boolean
  onChange: (roles: Record<string, string>) => void
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  const held = targets.filter((target) => roles[target.id])
  // Only targets the user is not on yet, and that some role can be held on.
  const addable = targets.filter(
    (target) => !roles[target.id] && teamRolesFor(app, target.type, catalog).length
  )

  const add = (target: RoleTarget) => {
    const [first] = teamRolesFor(app, target.type, catalog)
    if (first) onChange({ ...roles, [target.id]: first.id })
    setOpen(false)
  }

  const remove = (id: string) => {
    const rest = { ...roles }
    delete rest[id]
    onChange(rest)
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <FieldLabel>{title}</FieldLabel>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled || !addable.length}
            >
              <Plus className="size-4" />
              {addLabel}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80 p-0">
            <Command>
              <CommandInput placeholder={searchPlaceholder} />
              <CommandList>
                <CommandEmpty>{t('targeting.noResults')}</CommandEmpty>
                {addable.map((target) => (
                  <CommandItem
                    key={target.id}
                    value={`${target.name} ${target.description ?? ''} ${target.id}`}
                    onSelect={() => add(target)}
                  >
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate">{target.name}</span>
                      {target.description && (
                        <span className="truncate text-xs text-muted-foreground">
                          {target.description}
                        </span>
                      )}
                    </span>
                  </CommandItem>
                ))}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {held.length === 0 ? (
        <p className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
          {emptyText}
        </p>
      ) : (
        <table className="w-full border-collapse text-[13px]">
          <thead>
            <tr className="border-b border-(--ad-border) text-left text-xs text-(--ad-text-muted)">
              {/* The section label above already names the column. */}
              <th className="px-3 py-1.5 font-semibold">
                <span className="sr-only">{title}</span>
              </th>
              <th className="w-1/2 px-3 py-1.5 font-semibold">{t('userRoles.role')}</th>
              <th className="w-10 px-3 py-1.5">
                <span className="sr-only">{t('common.actions')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {held.map((target) => (
              <tr key={target.id} className="border-b border-(--ad-border) last:border-b-0">
                <td className="px-3 py-1.5 align-top">
                  <div className="font-medium">{target.name}</div>
                  {target.description && (
                    <div className="text-xs text-muted-foreground">{target.description}</div>
                  )}
                </td>
                <td className="px-3 py-1.5">
                  <RoleSelect
                    label={t('userRoles.roleOn', { name: target.name })}
                    value={roles[target.id]}
                    roles={teamRolesFor(app, target.type, catalog)}
                    disabled={disabled}
                    onChange={(roleId) => onChange({ ...roles, [target.id]: roleId })}
                  />
                </td>
                <td className="px-3 py-1.5 text-right">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label={t('userRoles.removeFrom', { name: target.name })}
                    title={t('userRoles.removeFrom', { name: target.name })}
                    disabled={disabled}
                    onClick={() => remove(target.id)}
                  >
                    <X className="size-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

function AppRoles({
  app,
  value,
  onChange,
  catalog,
  teams,
  teamGroups,
  disabled,
}: {
  app: string
  value: AppRolesDraft
  onChange: (value: AppRolesDraft) => void
  catalog: Role[]
  teams: RoleTarget[]
  teamGroups: RoleTarget[]
  disabled?: boolean
}) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col gap-4">
      <div className="p-field full">
        <FieldLabel tooltip={t('userRoles.networkRoleHelp')}>
          {t('userRoles.networkRole')}
        </FieldLabel>
        <RoleSelect
          label={t('userRoles.networkRole')}
          value={value.network}
          roles={networkRolesFor(app, catalog)}
          emptyLabel={t('userRoles.notAppUser')}
          disabled={disabled}
          onChange={(network) => onChange({ ...value, network })}
        />
      </div>

      {!value.network ? (
        <p className="text-xs text-muted-foreground">{t('userRoles.pickNetworkRole')}</p>
      ) : (
        <>
          {teamGroups.length > 0 && canHoldTeamGroupRoles(app, value.network, catalog) && (
            <TargetRoles
              title={t('userRoles.teamGroup')}
              targets={teamGroups}
              roles={value.teamGroups}
              app={app}
              catalog={catalog}
              addLabel={t('userRoles.addTeamGroup')}
              searchPlaceholder={t('userRoles.searchTeamGroups')}
              emptyText={t('userRoles.noTeamGroupRoles')}
              disabled={disabled}
              onChange={(teamGroupRoles) => onChange({ ...value, teamGroups: teamGroupRoles })}
            />
          )}

          <TargetRoles
            title={t('userRoles.team')}
            targets={teams}
            roles={value.teams}
            app={app}
            catalog={catalog}
            addLabel={t('userRoles.addTeam')}
            searchPlaceholder={t('userRoles.searchTeams')}
            emptyText={t('userRoles.noTeamRoles')}
            disabled={disabled}
            onChange={(teamRoles) => onChange({ ...value, teams: teamRoles })}
          />
        </>
      )}
    </div>
  )
}

interface UserRolesEditorProps {
  apps: string[]
  value: RolesDraft
  onChange: (value: RolesDraft) => void
  catalog: Role[]
  teams: RoleTarget[]
  teamGroups: RoleTarget[]
  disabled?: boolean
}

/** A user's network, team and team group roles, one tab per application. */
export function UserRolesEditor({
  apps,
  value,
  onChange,
  catalog,
  teams,
  teamGroups,
  disabled,
}: UserRolesEditorProps) {
  const { t } = useTranslation()
  const render = (app: string) => (
    <AppRoles
      app={app}
      value={value[app] ?? emptyAppRoles()}
      onChange={(appRoles) => onChange({ ...value, [app]: appRoles })}
      catalog={catalog}
      teams={teams}
      teamGroups={teamGroups}
      disabled={disabled}
    />
  )

  if (apps.length === 1) return render(apps[0])
  return (
    <Tabs defaultValue={apps[0]}>
      <TabsList>
        {apps.map((app) => (
          <TabsTrigger key={app} value={app}>
            {t(`userRoles.apps.${app}`, app)}
          </TabsTrigger>
        ))}
      </TabsList>
      {apps.map((app) => (
        <TabsContent key={app} value={app} className="pt-3">
          {render(app)}
        </TabsContent>
      ))}
    </Tabs>
  )
}
